// @givmo/app-admin — Employees → Roster: the OCCASION DATES CSV import dialog (EP-WEB11).
//
// Posts a whole CSV to `POST /admin/company/employees/occasion-dates/import` and renders the
// per-row echo. **The backend does the batching** — there is deliberately no client-side
// CSV→N-calls fan-out here (unlike the EP-WEB5 bulk precedent), because this route applies the
// whole file itself and reports every row.
//
// ── WHAT THE COPY HAS TO CARRY, AND WHY ────────────────────────────────────────────────────────
//
// None of the CSV contract is guessable, so the dialog states it rather than letting the admin
// discover it from a 400:
//
//   - The header is EXACTLY `work_email,birthday,hire_date`. A different header is a structural
//     400 and nothing is applied.
//   - `birthday` is `MM-DD` (or `MM/DD`) with NO YEAR. A year-carrying value is REFUSED, not
//     truncated — the employer must not believe Givmo stored a year it deliberately does not keep.
//   - A BLANK cell leaves that date alone; the literal `-` CLEARS it. Two intents, two spellings.
//   - Row numbers in the echo are 1-based INCLUDING the header, so they line up with the admin's
//     spreadsheet. They are rendered verbatim, never re-based.
//
// ── NON-ATOMIC, PER-ROW APPLY ──────────────────────────────────────────────────────────────────
//
// Each row is its own unit of work. A 2xx's counts are a receipt for what LANDED, so the success
// copy never says "all or nothing"; and a request that dies mid-file leaves the applied rows
// durable with no rollback, so the failure copy says re-upload the SAME file — every write is
// idempotent, so already-applied rows come back `unchanged` and the file converges.
//
// Unmatched (not on this roster) and invalid (unparseable) rows do NOT fail the request: they come
// back inside a 2xx with counts, because re-uploading an HR export that spans several employers is
// the normal case. Only WHOLE-FILE problems reject, and they reject PRE-APPLY under two statuses:
// 400 (structural — wrong header, no data rows, >10,000 rows) and 422 (the `csv` field's own
// `max_length` of 1,000,000 characters, which a legitimate 10k-row export clears easily). Both mean
// nothing was written, so both get the same "nothing was changed" copy — never the re-upload advice,
// which would loop straight back into the same rejection.
//
// A 404 is flag skew, not a failure: this route has no path entity, so it can only mean the backend
// routes are dark in this environment.

import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  isGivmoError,
  sdk,
  type EmployeeOccasionDatesImportOut,
  type EmployeeOccasionDatesImportRowOut,
} from '@givmo/api-client';
import { Badge, Button, Dialog, Icon, Textarea, testIdProp, toast } from '@givmo/design-system';
import { Check, Copy, FileUp } from 'lucide-react';
import {
  CSV_CLEAR_TOKEN,
  CSV_HEADER,
  CSV_MAX_CHARS,
  CSV_MAX_ROWS,
  IMPORT_APPLIED_NOTE,
  IMPORT_RETRY_NOTE,
  importCountsView,
  importOutcomeLabel,
  importOutcomeTone,
  importRowReason,
  importSummaryLine,
  isOccasionRouteUnavailable,
  isStructuralImportError,
  OCCASION_UNAVAILABLE_NOTE,
  orderedImportRows,
} from './occasion-dates-decode';
import { OccasionUnavailableNotice } from './occasion-unavailable-notice';

const PREFIX = 'roster-occasion-import';

const GENERIC_ERROR = 'Could not finish the import.';

/** How the request ended, when it did not end in a result. */
type ImportFailure =
  /** Pre-apply 400/422 — the backend applied nothing. Fix the file, upload again. */
  | { kind: 'structural'; message: string }
  /** Anything else (5xx, network, mid-flight). Rows may already be durable — re-upload the file. */
  | { kind: 'partial'; message: string };

export interface ImportOccasionDatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Refetch the roster so the "Dates on file" column reflects what the import applied. */
  onImported: () => void;
  unavailable: boolean;
  onUnavailable: () => void;
}

export function ImportOccasionDatesDialog({
  open,
  onOpenChange,
  onImported,
  unavailable,
  onUnavailable,
}: ImportOccasionDatesDialogProps) {
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<EmployeeOccasionDatesImportOut | null>(null);
  const [failure, setFailure] = useState<ImportFailure | null>(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setCsv('');
    setFileName(null);
    setResult(null);
    setFailure(null);
  };

  const importCsv = useMutation({
    mutationFn: (text: string) =>
      sdk.companiesImportCompanyEmployeeOccasionDates({ body: { csv: text }, throwOnError: true }),
    onSuccess: ({ data }) => {
      // The roster carries the dates, so a successful import changes rows already on screen.
      onImported();
      setResult((data ?? null) as EmployeeOccasionDatesImportOut | null);
      setFailure(null);
    },
    onError: (err: unknown) => {
      // Unlike the per-employee PUT, this route takes no path entity, so a 404 can only mean the
      // backend flag is off or the route is absent — never "that one is missing". Latch it.
      if (isOccasionRouteUnavailable(err)) {
        onUnavailable();
        return;
      }
      const message = isGivmoError(err) ? err.message : GENERIC_ERROR;
      setFailure({ kind: isStructuralImportError(err) ? 'structural' : 'partial', message });
      setResult(null);
    },
  });

  const submitting = importCsv.isPending;
  const trimmed = csv.trim();

  const submit = () => {
    if (submitting || trimmed.length === 0) return;
    setFailure(null);
    setResult(null);
    importCsv.mutate(csv);
  };

  const onPickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file after a fix
    if (!file) return;
    try {
      const text = await file.text();
      setCsv(text);
      setFileName(file.name);
      setResult(null);
      setFailure(null);
    } catch {
      toast.error('Could not read that file. Paste its contents below instead.');
    }
  };

  const copyHeader = async () => {
    try {
      await navigator.clipboard.writeText(CSV_HEADER);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // The header is on screen and selectable, so a clipboard denial is not a dead end.
      toast.error('Could not copy. Select the header row above and copy it manually.');
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label="Import occasion dates"
        className="flex max-h-[calc(100vh-64px)] flex-col gap-16 overflow-y-auto"
        width={620}
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Import occasion dates
        </Dialog.Title>

        {unavailable ? (
          <>
            <OccasionUnavailableNotice testId="roster-occasion-unavailable">
              {OCCASION_UNAVAILABLE_NOTE}
            </OccasionUnavailableNotice>
            <div className="mt-8 flex items-center justify-end">
              <Button
                appearance="saas"
                tone="secondary"
                size="md"
                onClick={() => onOpenChange(false)}
                testId={`${PREFIX}-unavailable-close`}
              >
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Upload a CSV of birthdays and hire dates for people already on your roster. Rows are
              matched by work email — anyone not on your roster is reported back and skipped, not
              added.
            </Dialog.Description>

            <FormatHelp copied={copied} onCopy={() => void copyHeader()} />

            <div className="flex flex-col gap-8">
              <div className="flex flex-wrap items-center gap-10">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="sr-only"
                  onChange={(event) => void onPickFile(event)}
                  data-testid={`${PREFIX}-file`}
                  aria-label="Choose a CSV file"
                />
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  disabled={submitting}
                  leadingIcon={<Icon icon={FileUp} size={14} aria-hidden />}
                  onClick={() => fileInputRef.current?.click()}
                  testId={`${PREFIX}-choose-file`}
                >
                  Choose CSV file
                </Button>
                <span className="font-sans text-s13 tracking-normal text-text-tertiary">
                  {fileName ?? 'or paste the file’s contents below'}
                </span>
              </div>

              <Textarea
                value={csv}
                onChange={(event) => {
                  setCsv(event.target.value);
                  setFileName(null);
                }}
                rows={6}
                disabled={submitting}
                placeholder={`${CSV_HEADER}\nalex@acme.com,03-14,2021-06-01\nsam@acme.com,,${CSV_CLEAR_TOKEN}`}
                aria-label="CSV contents"
                testId={`${PREFIX}-csv`}
              />
            </div>

            {failure !== null && <FailureBlock failure={failure} />}

            {result !== null && <ResultBlock result={result} />}

            <div className="mt-8 flex items-center justify-end gap-8">
              <Dialog.Close
                render={<Button appearance="saas" tone="secondary" size="md" />}
                {...testIdProp(`${PREFIX}-close`)}
              >
                {result === null ? 'Cancel' : 'Done'}
              </Dialog.Close>
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                loading={submitting}
                disabled={submitting || trimmed.length === 0}
                onClick={submit}
                testId={`${PREFIX}-submit`}
              >
                {failure?.kind === 'partial' ? 'Re-upload' : 'Import dates'}
              </Button>
            </div>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** The CSV contract, stated. Every line here is a rule an admin cannot infer from the file format. */
function FormatHelp({ copied, onCopy }: { copied: boolean; onCopy: () => void }) {
  return (
    <div
      className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-12"
      data-testid={`${PREFIX}-format`}
    >
      <div className="flex flex-wrap items-center justify-between gap-8">
        <code className="font-mono text-s13 tracking-normal text-text-strong">{CSV_HEADER}</code>
        <Button
          appearance="saas"
          tone="ghost"
          size="sm"
          leadingIcon={<Icon icon={copied ? Check : Copy} size={14} aria-hidden />}
          onClick={onCopy}
          testId={`${PREFIX}-copy-header`}
        >
          {copied ? 'Copied' : 'Copy header'}
        </Button>
      </div>
      <ul className="flex list-disc flex-col gap-4 pl-16 font-sans text-s13 leading-h15 tracking-normal text-text-secondary">
        <li>The first row must be exactly that header.</li>
        <li>
          <span className="font-w600 text-text-strong">birthday</span> is{' '}
          <span className="font-w600 text-text-strong">MM-DD</span> (or MM/DD) — no year. Givmo
          stores no birth year, so a value that carries one is rejected rather than trimmed.
        </li>
        <li>
          <span className="font-w600 text-text-strong">hire_date</span> is{' '}
          <span className="font-w600 text-text-strong">YYYY-MM-DD</span>.
        </li>
        <li>
          Leave a cell <span className="font-w600 text-text-strong">blank</span> to leave that date
          unchanged. Put a single{' '}
          <span className="font-w600 text-text-strong">{CSV_CLEAR_TOKEN}</span> to clear it.
        </li>
        <li>
          Up to {CSV_MAX_ROWS.toLocaleString('en-US')} rows. Row numbers below count the header as
          row 1, so they match your spreadsheet.
        </li>
      </ul>
    </div>
  );
}

function FailureBlock({ failure }: { failure: ImportFailure }) {
  const structural = failure.kind === 'structural';
  return (
    <div
      className="flex flex-col gap-6 rounded-lg border border-danger-100 bg-danger-50 p-12"
      data-testid={structural ? `${PREFIX}-error` : `${PREFIX}-partial`}
    >
      <p className="font-sans text-s14 font-w600 tracking-normal text-danger-700">
        {structural
          ? 'Givmo couldn’t read that file — nothing was changed.'
          : 'The import stopped.'}
      </p>
      <p className="font-sans text-s13 leading-h15 tracking-normal text-danger-700">
        {failure.message}
      </p>
      <p className="font-sans text-s13 leading-h15 tracking-normal text-danger-700">
        {structural
          ? `Check that the first row is exactly “${CSV_HEADER}”, that there is at least one row of data, and that the file is no larger than ${CSV_MAX_ROWS.toLocaleString('en-US')} rows or ${(CSV_MAX_CHARS / 1_000_000).toFixed(0)} MB. Splitting a very large export into two files works.`
          : IMPORT_RETRY_NOTE}
      </p>
    </div>
  );
}

/** The 2xx echo: the five counts, the headline, and the per-row outcomes. */
function ResultBlock({ result }: { result: EmployeeOccasionDatesImportOut }) {
  const { visible, hiddenCount } = orderedImportRows(result.rows);
  return (
    <div className="flex flex-col gap-10" data-testid={`${PREFIX}-summary`}>
      <p
        className="font-sans text-s14 font-w600 tracking-normal text-text-strong"
        {...testIdProp(`${PREFIX}-summary-line`)}
      >
        {importSummaryLine(result)}
      </p>

      <div className="flex flex-wrap gap-8" data-testid={`${PREFIX}-counts`}>
        {importCountsView(result).map((entry) => (
          <Badge
            key={entry.outcome}
            tone={importOutcomeTone(entry.outcome)}
            size="md"
            testId={`${PREFIX}-count-${entry.outcome}`}
          >
            {entry.count} {entry.label.toLowerCase()}
          </Badge>
        ))}
      </div>

      <p className="font-sans text-s13 leading-h15 tracking-normal text-text-tertiary">
        {IMPORT_APPLIED_NOTE}
      </p>

      {visible.length > 0 && (
        <div className="flex max-h-[240px] flex-col divide-y divide-border-subtle overflow-y-auto rounded-lg border border-border-subtle">
          {visible.map((row) => (
            <ImportRow key={row.row} row={row} />
          ))}
        </div>
      )}

      {hiddenCount > 0 && (
        <p
          className="font-sans text-s13 leading-h15 tracking-normal text-text-tertiary"
          {...testIdProp(`${PREFIX}-truncated`)}
        >
          Showing the first {visible.length} rows, those needing attention listed first.{' '}
          {hiddenCount} more {hiddenCount === 1 ? 'row is' : 'rows are'} not shown — the counts
          above cover every row in the file.
        </p>
      )}
    </div>
  );
}

/**
 * One echoed row. The row NUMBER is the server's, rendered verbatim (1-based, header included) — but
 * the REASON is not: the backend sends machine tokens on the rows it generates itself, so it goes
 * through `importRowReason` rather than straight to the screen.
 */
function ImportRow({ row }: { row: EmployeeOccasionDatesImportRowOut }) {
  const reason = importRowReason(row.reason);
  return (
    <div
      className="flex items-start justify-between gap-10 px-12 py-8"
      data-testid={`${PREFIX}-row-${row.row}`}
    >
      <div className="flex min-w-0 flex-col">
        <span className="font-sans text-s13 font-w600 tracking-normal text-text-strong tabular-nums">
          Row {row.row}
          {row.work_email ? (
            <span className="font-w400 text-text-secondary"> · {row.work_email}</span>
          ) : null}
        </span>
        {reason !== null ? (
          <span className="font-sans text-s13 leading-h15 tracking-normal text-text-tertiary">
            {reason}
          </span>
        ) : null}
      </div>
      <Badge tone={importOutcomeTone(row.outcome)} size="sm">
        {importOutcomeLabel(row.outcome)}
      </Badge>
    </div>
  );
}
